/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useRef, useEffect, useState, useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
// import { Chart as ChartJS, registerables } from 'chart.js';
// import { Chart } from 'react-chartjs-2'
// ChartJS.register(...registerables);
// import 'chartjs-adapter-moment';

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import SoftSelect from "components/SoftComponents/SoftSelect";

import DashboardLayout from "template/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "template/examples/Navbars/DashboardNavbar";
import Footer from "template/examples/Footer";

// Soft UI Dashboard PRO React helper functions
import gradientChartLine from "assets/theme/functions/gradientChartLine";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

//Backend access
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { getLogs, loadToken } from 'services/api/bks';

import { Route, Link, Routes, useLocation, useNavigate, useMatch } from 'react-router-dom';
import moment from "moment";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import DoughnutChart from "../DoughnutChart";
import reportsAPI from 'services/api/reports';

async function processChartData(data, uuid, startTime, endTime) {
    // 1773151200

    var colours = [
        '#2d385a',
        '#67727a',
        '#1a348a',
        '#2f323b'
    ];

    const daysBetween = endTime.diff(startTime, 'days') + 1;
    var dataset_data = {};

    for (var uuid in data) {
        var device = data[uuid];

        for (var key in device) {
            for (var zone in device[key]['infringement_zones']) {

                if (!(dataset_data?.[zone])) {
                    dataset_data[zone] = 0;
                }
                dataset_data[zone] += device[key]['infringement_zones'][zone];
            }
        }
    }

    var chart_data = {
        labels: [],
        datasets: [
            {
                data: [],
                backgroundColor: []
            }
        ]
    }

    var total_count = 0;

    for (var zone in dataset_data) {
        var avg = dataset_data[zone] / daysBetween;

        chart_data.labels.push(zone);
        chart_data.datasets[0].data.push(avg);
        total_count += avg;

        chart_data.datasets[0].backgroundColor.push(colours[chart_data.datasets[0].backgroundColor.length]);
    }

    return { chart_data, total_count };
}

function ReportingDoughnutChart({ device, title, startTime, setStartTime, endTime, setEndTime, groupSize, setGroupSize, height }) {
    // console.log(groupSize)
    loadToken(useAuth0());

    const chartRef = useRef(null);
    const [chartData, setChartData] = useState(null);
    const [totalCount, setTotalCount] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchData() {
            setLoading(true);

            var data = await reportsAPI.getCameraObjectsData([device.uuid], startTime.unix(), endTime.unix(), { devices: [device] });
            var { chart_data, total_count } = await processChartData(data, device.uuid, startTime, endTime);

            setChartData(chart_data);
            setTotalCount(total_count);
            setLoading(false);
        };
        fetchData();
    }, [startTime, endTime, device]);

    return (
        <Card>
            <SoftBox p={{ xxs: 1, xs: 2 }}>
                <SoftBox sx={{ opacity: loading ? 0.3 : 1 }}>
                    <SoftBox m={1} display="flex" justifyContent="center" alignItems="center">
                        <SoftTypography variant="h6">
                            {title}
                        </SoftTypography>
                    </SoftBox>
                    <SoftBox sx={{ minHeight: '200px', height: height, width: 'auto' }}>
                        {chartData && <DoughnutChart chartData={chartData} totalCount={totalCount} startTime={startTime.unix() * 1000} endTime={endTime.unix() * 1000} groupSize={groupSize} setStartTime={setStartTime} setEndTime={setEndTime} setGroupSize={setGroupSize} />}
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </Card>
    );

}

// Typechecking props for the ReportingDoughnutChart
ReportingDoughnutChart.propTypes = {
    device: PropTypes.object,
    title: PropTypes.string,
    startTime: PropTypes.instanceOf(moment()),
    setStartTime: PropTypes.func,
    endTime: PropTypes.instanceOf(moment()),
    setEndTime: PropTypes.func,
    groupSize: PropTypes.number,
    setGroupSize: PropTypes.func,
    height: PropTypes.number
};

export default ReportingDoughnutChart;

